import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/core/stores/auth-store';
import { useOperatorStore } from '@/core/stores/operator-store';
import { messageHandler } from '@/core/auth/message-handler';

/**
 * Hook to handle authentication initialization and setup
 *
 * 1. Waits for Zustand rehydration from localStorage (both auth and operator stores)
 * 2. Initializes authentication on mount
 * 3. Sets up message handler for postMessage tokens
 * 4. Reloads page when distributor changes
 * 5. Handles post-authentication navigation (only for fresh logins, not hot reloads)
 * 6. Handles tokens arriving during initialization (race condition fix)
 *
 */
export const useAuthInitialization = () => {
  const {
    isLoading,
    isInitialized,
    isAuthenticated,
    distributorId,
    initialize,
    loginWithToken
  } = useAuthStore();

  const navigate = useNavigate();
  const location = useLocation();
  const previousAuthState = useRef<boolean | null>(null);
  const previousDistributorId = useRef<string | null>(null);
  const pendingToken = useRef<string | null>(null);
  const [isAuthHydrated, setIsAuthHydrated] = useState(false);
  const [isOperatorHydrated, setIsOperatorHydrated] = useState(false);

  const isHydrated = isAuthHydrated && isOperatorHydrated;

  // Wait for Zustand stores to rehydrate from localStorage
  useEffect(() => {
    // Auth store hydration
    const unsubAuthHydration = useAuthStore.persist.onFinishHydration(() => {
      setIsAuthHydrated(true);
    });
    if (useAuthStore.persist.hasHydrated()) {
      setIsAuthHydrated(true);
    }

    // Operator store hydration
    const unsubOperatorHydration = useOperatorStore.persist.onFinishHydration(() => {
      setIsOperatorHydrated(true);
    });
    if (useOperatorStore.persist.hasHydrated()) {
      setIsOperatorHydrated(true);
    }

    return () => {
      unsubAuthHydration();
      unsubOperatorHydration();
    };
  }, []);

  useEffect(() => {
    // 1. Wait for hydration before initializing
    if (!isHydrated) {
      console.log('[Auth] Waiting for hydration...', { isAuthHydrated, isOperatorHydrated });
      return;
    }

    // Debug: Log the hydrated state
    const authState = useAuthStore.getState();
    console.log('[Auth] Hydration complete, auth state:', {
      token: authState.token ? 'present' : 'missing',
      operatorId: authState.operatorId,
      accountToken: authState.accountToken ? 'present' : 'missing',
      accountId: authState.accountId,
      isInitialized: authState.isInitialized,
      isAuthenticated: authState.isAuthenticated
    });

    // 2. Initialize authentication on mount (only once)
    if (!isInitialized) {
      console.log('[Auth] Starting initialization...');
      initialize();
    }

    // 2. Set up message handler for receiving tokens via postMessage
    messageHandler.setTokenReceivedCallback((token: string) => {
      // If still initializing, store token for later processing
      if (!isInitialized) {
        pendingToken.current = token;
        return;
      }

      // If initialized, process token immediately
      loginWithToken(token);
    });

    // 3. Cleanup message handler on unmount
    return () => {
      messageHandler.setTokenReceivedCallback(() => {});
    };
  }, [isHydrated, isInitialized, initialize, loginWithToken]);

  // 4. Reset operator selection when distributor changes
  useEffect(() => {
    if (!distributorId) return;
    if (previousDistributorId.current !== null && previousDistributorId.current !== distributorId) {
      useOperatorStore.getState().resetForDistributorChange();
    }
    previousDistributorId.current = distributorId;
  }, [distributorId]);

  // 6. Process pending token after initialization completes
  useEffect(() => {
    if (isInitialized && pendingToken.current) {
      const token = pendingToken.current;
      pendingToken.current = null;
      loginWithToken(token);
    }
  }, [isInitialized, loginWithToken]);

  // 7. Handle navigation after successful authentication (only for fresh logins)
  // In iframe mode, the parent app controls navigation via URL, so we should NOT
  // redirect the user away from their intended destination after authentication.
  useEffect(() => {
    // Skip if not initialized yet
    if (!isInitialized) {
      return;
    }

    // Track authentication state changes (false -> true = fresh login)
    const wasUnauthenticated = previousAuthState.current === false;
    const isNowAuthenticated = isAuthenticated === true;
    previousAuthState.current = isAuthenticated;

    // Only redirect on fresh login (transition from unauthenticated to authenticated)
    // Skip for: hot reloads (null -> true), staying authenticated (true -> true)
    if (!wasUnauthenticated || !isNowAuthenticated) {
      return;
    }

    // Only redirect from login or error pages - let user stay on current URL
    // This preserves the navigation set by parent iframe (e.g., /energy-management)
    const needsRedirectPaths = ['/login', '/auth-error'];
    if (!needsRedirectPaths.includes(location.pathname)) {
      // User is on a valid page (set by parent iframe), don't redirect
      return;
    }

    // Check if there's a saved return URL from 401 redirect
    const returnUrl = localStorage.getItem('returnUrl');
    if (returnUrl) {
      console.log(`🔄 Restoring saved URL after login: ${returnUrl}`);
      localStorage.removeItem('returnUrl');
      navigate(returnUrl, { replace: true });
      return;
    }

    // Only navigate to root if user is on login/error page and no returnUrl
    // ProtectedRoute will handle operator selection check automatically
    navigate('/', { replace: true });
  }, [isInitialized, isAuthenticated, location.pathname, navigate]);

  return {
    isLoading: isLoading || !isHydrated,
    isInitialized: isInitialized && isHydrated,
    isAuthenticated,
  };
};
